import "../../GoldenRecordPage.scss";
import React from "react";
import InfiniteScroll from "react-infinite-scroll-component";
import {observer} from "mobx-react-lite";
import {
    Box,
    Button,
    Checkbox,
    CircularProgress,
    Drawer,
    IconButton,
    Paper,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Tooltip,
    Typography,
} from "@mui/material";
import {CurrencyComponent} from "../../../../components/currency-component/CurrencyComponent.tsx";
import SettingsIcon from "~/icons/svg/SettingsIcon.svg?react";
import CloseIcon from "~/icons/svg/CloseIcon.svg?react";

import {useStores} from "../../../../stores/index.tsx";
import {type GoldenRecordClusterPropertyType, GoldenRecordClusterType,} from "../../../../services/ApiTypes.ts";
import {notificationStore} from "../../../../stores/init.ts";
import {GroupingSettingsSkeletonRow, SKELETON_INITIAL_ROWS, SKELETON_PAGE_ROWS,} from "./GroupingSettingSkeleton.tsx";

const PROPERTY_SCROLL_ID = "grouping-settings-property-scroll";

export const GroupingSettingsModal: React.FC = observer(() => {
    const {goldenRecordStore} = useStores();
    const filterController = goldenRecordStore.grPageController.filterController;
    const tableController = goldenRecordStore.grPageController.tableController;
    const [isTypeDrawerOpen, setIsTypeDrawerOpen] = React.useState(false);
    const [draftById, setDraftById] = React.useState(
        () => new Map<number, boolean>(),
    );
    const [savePending, setSavePending] = React.useState(false);
    const wasDrawerOpenRef = React.useRef(false);

    const propertyRows = filterController.property_types;
    const propertyRowsIdsKey = propertyRows.map((c) => c.id).join(",");
    const isInitialTableLoading =
        filterController.property_types_request_busy &&
        filterController.property_types.length === 0;
    const showPropertyTable = propertyRows.length > 0 || isInitialTableLoading;
    const isServiceCluster =
        filterController.cluster_type === GoldenRecordClusterType.Service;
    const isGroupingDisabled =
        !filterController.selected_cluster_id || isServiceCluster;

    const hasMorePropertyTypes =
        !isInitialTableLoading &&
        (filterController.property_types_count_server ?? 0) >
        filterController.property_types.length;

    const loadMorePropertyTypes = () => {
        if (filterController.property_types_request_busy) return;
        filterController.setPropertyTypePage(
            filterController.property_types_page + 1,
        );
    };

    // does user changed the value of the checkbox or it what saved before on server
    const effectiveSelected = (cpt: GoldenRecordClusterPropertyType) =>
        draftById.get(cpt.id) ?? cpt.selected;

    const allPropertiesSelected =
        propertyRows.length > 0 && propertyRows.every((c) => effectiveSelected(c));

    const somePropertiesSelected = propertyRows.some((c) => effectiveSelected(c));

    React.useEffect(() => {
        const rows = filterController.property_types;
        const open = isTypeDrawerOpen;
        const wasOpen = wasDrawerOpenRef.current;
        if (open) {
            if (!wasOpen) {
                setDraftById(new Map(rows.map((c) => [c.id, c.selected])));
            } else {
                // Pagination / infinite scroll: seed draft only for ids we have not seen yet
                // (cpt.selected). Rows the user already toggled stay in the map unchanged.
                setDraftById((prev) => {
                    const next = new Map(prev);
                    for (const cpt of rows) {
                        if (!next.has(cpt.id)) next.set(cpt.id, cpt.selected);
                    }
                    return next;
                });
            }
            wasDrawerOpenRef.current = true;
        } else {
            wasDrawerOpenRef.current = false;
        }
    }, [isTypeDrawerOpen, propertyRowsIdsKey, filterController]);

    const onSelectAllProperties = (checked: boolean) => {
        setDraftById((prev) => {
            const next = new Map(prev);
            for (const c of propertyRows) {
                next.set(c.id, checked);
            }
            return next;
        });
    };

    const handleSave = async () => {
        setSavePending(true);
        try {
            const updates: {
                id: number;
                label: string;
                serverSelected: boolean;
                targetSelected: boolean;
            }[] = [];
            for (const cpt of propertyRows) {
                const nextSel = draftById.get(cpt.id) ?? cpt.selected;
                if (nextSel !== cpt.selected) {
                    updates.push({
                        id: cpt.id,
                        label: cpt.type.name + (cpt.type.unit ? ` (${cpt.type.unit})` : ""),
                        serverSelected: cpt.selected,
                        targetSelected: nextSel,
                    });
                }
            }
            if (updates.length === 0) {
                setIsTypeDrawerOpen(false);
                tableController.resetRowState(true);
                return;
            }
            const results = await Promise.allSettled(
                updates.map((u) =>
                    filterController.setSelectedPropertyType(u.id, u.targetSelected),
                ),
            );
            const failed: typeof updates = [];
            results.forEach((r, i) => {
                if (r.status === "rejected") failed.push(updates[i]);
            });
            if (failed.length > 0) {
                setDraftById((prev) => {
                    const next = new Map(prev);
                    for (const u of failed) {
                        const row = filterController.property_types.find(
                            (pt) => pt.id === u.id,
                        );
                        next.set(u.id, row?.selected ?? u.serverSelected);
                    }
                    return next;
                });
                for (const u of failed) {
                    notificationStore.show(
                        `Could not save grouping for "${u.label}". Please try again.`,
                        {
                            severity: "error",
                            id: `grouping-settings-save-error-${u.id}`,
                        },
                    );
                }
            } else {
                setIsTypeDrawerOpen(false);
                tableController.resetRowState(true);
            }
        } finally {
            setSavePending(false);
        }
    };

    return (
        <Stack sx={{height: "100%"}}>
            <Tooltip
                title={
                    isServiceCluster
                        ? "Grouping is not available for service groups"
                        : ""
                }
            >
        <span>
          <Button
              variant="outlined"
              startIcon={<SettingsIcon/>}
              disabled={isGroupingDisabled}
              sx={{
                  backgroundColor: "#000",
                  color: "#fff",
                  textTransform: "none",
                  px: "22px",
                  py: "8px",
                  borderRadius: 2,
                  "&:disabled": {
                      background:
                          "var(--action-disabledBackground, rgba(0, 0, 0, 0.12))",
                      borderColor: "transparent",
                      color: "rgba(0, 0, 0, 0.38)",
                  },
              }}
              onClick={() => setIsTypeDrawerOpen(true)}
          >
            Grouping Settings
          </Button>
        </span>
            </Tooltip>

            <Drawer
                anchor="right"
                open={isTypeDrawerOpen}
                onClose={() => {
                    if (!savePending) setIsTypeDrawerOpen(false);
                }}
                sx={{zIndex: (theme) => theme.zIndex.modal}}
                slotProps={{
                    paper: {
                        sx: {
                            width: {xs: "100%", sm: "33.33vw"},
                            minWidth: {sm: "472px"},
                            maxWidth: "100vw",
                            height: "100%",
                            display: "flex",
                            flexDirection: "column",
                            backgroundColor: "#F9F9FF",
                        },
                    },
                }}
            >
                <Stack
                    direction="row"
                    justifyContent="space-between"
                    sx={{px: 2, pt: 2.5, pb: 1.5}}
                >
                    <Typography component="h2" variant="h6">
                        Grouping settings
                    </Typography>

                    <IconButton
                        disabled={savePending}
                        onClick={() => setIsTypeDrawerOpen(false)}
                    >
                        <CloseIcon/>
                    </IconButton>
                </Stack>

                <>
                    {!filterController.property_types_request_busy &&
                        filterController.property_types.length === 0 && (
                            <Typography variant="caption" sx={{p: 2}}>
                                There are no properties identified for grouping.
                            </Typography>
                        )}
                    {showPropertyTable && (
                        <>
                            <Typography variant="body1" sx={{mx: 2, mb: 1.5}}>
                                Configure which properties to use as grouping keys.
                            </Typography>
                            <Box
                                id={PROPERTY_SCROLL_ID}
                                sx={{
                                    flex: 1,
                                    minHeight: 0,
                                    overflow: "auto",
                                    px: 2,
                                }}
                            >
                                <InfiniteScroll
                                    scrollableTarget={PROPERTY_SCROLL_ID}
                                    dataLength={propertyRows.length}
                                    next={loadMorePropertyTypes}
                                    hasMore={hasMorePropertyTypes}
                                    loader={<></>}
                                    scrollThreshold="30px"
                                    style={{overflow: "visible"}}
                                >
                                    <Paper
                                        elevation={0}
                                        sx={{
                                            borderRadius: "8px",
                                            position: "relative",
                                            backgroundColor: "transparent",
                                        }}

                                    >
                                        <Box
                                            sx={{
                                                position: "sticky",
                                                top: "0",
                                                left: 0,
                                                right: 0,
                                                backgroundColor: "#F9F9FF",
                                                height: "4px",
                                            }}
                                        ></Box>
                                        <Table
                                            size="small"
                                            stickyHeader
                                            sx={{
                                                tableLayout: "fixed",
                                                marginBottom: "16px",
                                                "& th:first-child": {borderLeft: "1px solid #c4c6d0"},
                                                "& th:last-child": {borderRight: "1px solid #c4c6d0"},
                                                "& td:first-child": {
                                                    borderLeft: "1px solid #c4c6d0 !important",
                                                },
                                                "& td:last-child": {
                                                    borderRight: "1px solid #c4c6d0 !important",
                                                },
                                                "& tr:last-child td": {
                                                    borderBottom: "1px solid #c4c6d0",
                                                },
                                                "& tr:last-child td:first-child": {
                                                    borderBottomLeftRadius: "8px",
                                                },
                                                "& tr:last-child td:last-child": {
                                                    borderBottomRightRadius: "8px",
                                                },
                                            }}
                                        >
                                            <TableHead>
                                                <TableRow
                                                    sx={{
                                                        "& .MuiTableCell-root": {
                                                            backgroundColor: "#F9F9FF",
                                                            borderTop: "1px solid #c4c6d0",
                                                        },
                                                        "& .MuiTableCell-root:first-child": {
                                                            borderTopLeftRadius: "8px",
                                                        },
                                                        "& .MuiTableCell-root:last-child": {
                                                            borderTopRightRadius: "8px",
                                                        },
                                                    }}
                                                >
                                                    <TableCell sx={{width: "42%"}}>
                                                        <Box
                                                            sx={{
                                                                display: "flex",
                                                                alignItems: "center",
                                                                gap: 0.5,
                                                                minWidth: 0,
                                                            }}
                                                        >
                                                            <Checkbox
                                                                size="small"
                                                                checked={allPropertiesSelected}
                                                                indeterminate={
                                                                    somePropertiesSelected && !allPropertiesSelected
                                                                }
                                                                disabled={
                                                                    savePending ||
                                                                    propertyRows.length === 0 ||
                                                                    isInitialTableLoading
                                                                }
                                                                onChange={(_e, checked) =>
                                                                    onSelectAllProperties(checked)
                                                                }
                                                                slotProps={{
                                                                    input: {
                                                                        "aria-label":
                                                                            "Select all grouping properties",
                                                                    },
                                                                }}
                                                                sx={{p: 0.5, flexShrink: 0}}
                                                            />
                                                            Property
                                                        </Box>
                                                    </TableCell>
                                                    <TableCell align="right" sx={{width: "22%"}}>
                                                        Spend
                                                    </TableCell>
                                                    <TableCell align="right" sx={{width: "18%"}}>
                                                        Supp.
                                                    </TableCell>
                                                    <TableCell align="right" sx={{width: "18%"}}>
                                                        Rows
                                                    </TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {isInitialTableLoading &&
                                                    Array.from(
                                                        {length: SKELETON_INITIAL_ROWS},
                                                        (_, i) => (
                                                            <GroupingSettingsSkeletonRow key={`sk-init-${i}`}/>
                                                        ),
                                                    )}
                                                {!isInitialTableLoading &&
                                                    filterController.property_types.map((cpt) => {
                                                        const label =
                                                            cpt.type.name +
                                                            (cpt.type.unit ? ` (${cpt.type.unit})` : "");
                                                        const rowSelected = effectiveSelected(cpt);
                                                        return (
                                                            <TableRow key={cpt.id} hover selected={rowSelected}>
                                                                <TableCell sx={{py: 1}}>
                                                                    <Box
                                                                        sx={{
                                                                            display: "flex",
                                                                            alignItems: "center",
                                                                            gap: 1,
                                                                            minWidth: 0,
                                                                        }}
                                                                    >
                                                                        <Checkbox
                                                                            size="small"
                                                                            checked={rowSelected}
                                                                            disabled={cpt.busy || savePending}
                                                                            onChange={(_e, checked) => {
                                                                                setDraftById((prev) => {
                                                                                    const next = new Map(prev);
                                                                                    next.set(cpt.id, checked);
                                                                                    return next;
                                                                                });
                                                                            }}
                                                                            slotProps={{
                                                                                input: {
                                                                                    "aria-label": `Select ${label}`,
                                                                                },
                                                                            }}
                                                                            sx={{p: 0.5, flexShrink: 0}}
                                                                        />

                                                                        <Typography
                                                                            variant="body2"
                                                                            noWrap
                                                                            title={label}
                                                                        >
                                                                            {label}
                                                                        </Typography>
                                                                    </Box>
                                                                </TableCell>
                                                                <TableCell align="right">
                                                                    <CurrencyComponent
                                                                        v={cpt.spend_sum}
                                                                        nDigits={3}
                                                                    />
                                                                </TableCell>
                                                                <TableCell align="right">
                                                                    {cpt.supplier_count}
                                                                </TableCell>
                                                                <TableCell align="right">
                                                                    {cpt.transaction_count}
                                                                </TableCell>
                                                            </TableRow>
                                                        );
                                                    })}
                                                {/* “loading more rows” placeholder */}
                                                {!isInitialTableLoading &&
                                                    filterController.property_types_request_busy &&
                                                    Array.from({length: SKELETON_PAGE_ROWS}, (_, i) => (
                                                        <GroupingSettingsSkeletonRow key={`sk-more-${i}`}/>
                                                    ))}
                                            </TableBody>
                                        </Table>
                                    </Paper>
                                </InfiniteScroll>
                            </Box>
                        </>
                    )}
                </>
                <Box
                    sx={{
                        display: "flex",
                        justifyContent: "flex-end",
                        gap: 2,
                        px: 2,
                        py: 2,
                        boxShadow: "0 1px 3px 0 rgba(0, 0, 0, 0.12)",
                        marginTop: "auto",
                    }}
                >
                    <Button
                        onClick={() => setIsTypeDrawerOpen(false)}
                        disabled={savePending}
                    >
                        Dismiss
                    </Button>
                    <Button
                        variant="outlined"
                        disabled={savePending}
                        startIcon={
                            savePending ? (
                                <CircularProgress size={16} color="inherit"/>
                            ) : undefined
                        }
                        onClick={() => void handleSave()}
                    >
                        Save
                    </Button>
                </Box>
            </Drawer>
        </Stack>
    );
});
